import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { Link, Typography } from '@mui/material';
import dayjs, { Dayjs, isDayjs } from 'dayjs';
import { toArray, toString } from 'lodash';
import { memo, useMemo, useState } from 'react';
import { CheckboxList } from 'src/components/form';
import { Progress } from 'src/components/progress/Progress';
import { useParamQuery } from 'src/hooks';
import { lang } from 'src/utils';

import { FilterDropdownWithSearch } from '../filterDropdownWithSearch';
import { RadioGroup } from '../form/radioGroup';
import { SearchBarWithBadges } from '../input';
import { DateRangeWidget } from '../widget';
import { paramsKey } from './FilterList.const';
import { FilterListProps } from './FilterList.interface';
import {
  Accordion,
  Details,
  FilterAccordionWrapper,
  HeaderContainer,
  SearchWrapper,
  Summary,
  SummaryLabel,
} from './FilterList.styled';

export const FilterList = memo((props: FilterListProps) => {
  const { getParamsValue } = useParamQuery();
  const [resetVal, setResetVal] = useState<boolean>(false);

  const formatDate = (value: string[]) => {
    return toString(value) ? dayjs(toString(value)) : null;
  };

  const onReset = () => {
    props?.onReset && props.onReset();
    setResetVal(true);
  };

  const expandedMap = useMemo(() => {
    const map = new Map<string, boolean>();
    props?.filters?.forEach(({ label, defaultExpanded }) => {
      if (defaultExpanded) {
        map.set(label, defaultExpanded);
      } else {
        const values = getParamsValue(paramsKey(label));
        if (values?.length) {
          map.set(label, true);
        } else {
          map.set(label, false);
        }
      }
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paramValue = useMemo(
    () =>
      `${
        !props.hasExactSearch
          ? 'name.regexp'
          : props.isFuzzySearch
          ? 'name.regexp'
          : 'name.list'
      }`,
    [props.isFuzzySearch, props.hasExactSearch],
  );

  const allSearchTerms = useMemo(() => {
    const regexpTerms = getParamsValue('name.regexp');
    const listTerms = getParamsValue('name.list');

    return [...regexpTerms, ...listTerms];
  }, [getParamsValue]);

  return (
    <>
      <HeaderContainer>
        <Typography variant="h5" component="h2">
          {lang.refine}
        </Typography>
        {props?.showReset && (
          <Link
            data-testid="reset-filters"
            component="button"
            variant="body2"
            onClick={onReset}>
            {lang.reset}
          </Link>
        )}
      </HeaderContainer>
      <SearchWrapper>
        <SearchBarWithBadges
          placeholder="Filter"
          value={props?.searchVal ?? getParamsValue(paramValue)}
          searchTerms={props?.searchVal ?? allSearchTerms}
          autocompleteOptions={props.filters?.map((f) => f.label)}
          onChange={props.onChangeKeyword}
          onRemoveTerm={props.onRemoveKeyword}
          resetVal={resetVal}
          onResetComplete={() => setResetVal(false)}
          enableSearchArea={props.enableSearchArea}
          onSearchTypeChange={props.onSearchTypeChange}
          isFuzzySearch={props.isFuzzySearch}
          hasExactSearch={props.hasExactSearch}
          isLoading={props.isMatchStatusLoading}
          matchedNames={props.matchedNames}
        />
      </SearchWrapper>
      <FilterAccordionWrapper>
        {props.filters?.map((filter, key) => (
          <Accordion
            key={key}
            disableGutters
            disabled={filter.disabled}
            defaultExpanded={expandedMap.get(filter.label)}>
            <Summary
              expandIcon={!filter.disabled && <KeyboardArrowDownIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header">
              <SummaryLabel>
                {filter.labelOverride ?? filter.label}
              </SummaryLabel>
            </Summary>
            <Details>
              {filter.isLoading && <Progress />}
              {filter.isError && <div>{lang.error}</div>}
              {(function () {
                switch (filter?.fieldType) {
                  case 'dateRange': {
                    const startDate = formatDate(
                      getParamsValue(paramsKey(filter.label).split('|')[0]),
                    );
                    const endDate = formatDate(
                      getParamsValue(paramsKey(filter.label).split('|')[1]),
                    );

                    return (
                      <DateRangeWidget
                        labels={[lang.startDate, lang.endDate]}
                        values={[
                          formatDate(
                            getParamsValue(
                              paramsKey(filter.label).split('|')[0],
                            ),
                          ),
                          formatDate(
                            getParamsValue(
                              paramsKey(filter.label).split('|')[1],
                            ),
                          ),
                        ]}
                        onChange={(values) => {
                          props.onChangeFilter(
                            filter.label,
                            toArray(values).map((item) =>
                              isDayjs(item)
                                ? (item as Dayjs).utc().format()
                                : '',
                            ),
                          );
                        }}
                        options={{
                          disableFuture:
                            !!filter.dateRangeOption?.disableFutureDate,
                          ...(startDate && { endDateMin: startDate }),
                          ...(endDate && { startDateMax: endDate }),
                        }}
                        readOnly={props?.readOnly}
                      />
                    );
                  }
                  case 'radio':
                    return (
                      <RadioGroup
                        options={filter.options}
                        onChange={props.onChangeFilter}
                        summaryLabel={filter.label}
                        resetVal={resetVal}
                        setResetVal={setResetVal}
                      />
                    );
                  case 'filterWithSearch': {
                    const label = paramsKey(filter?.label);
                    const values =
                      // filter.filterSearchVal ??
                      getParamsValue(label);

                    return (
                      <FilterDropdownWithSearch
                        name={filter.label}
                        multiline={Boolean(filter.badgeOptions?.align)}
                        onChange={(value) => {
                          props.onChangeFilter(label, value as any);
                        }}
                        value={Array.isArray(values) ? values : []}
                        badgeOptions={filter.badgeOptions}
                      />
                    );
                  }
                  case 'freeSearch':
                    return (
                      <SearchWrapper>
                        <SearchBarWithBadges
                          placeholder="Filter"
                          value={filter.filterSearchVal}
                          searchTerms={filter.filterSearchVal}
                          onChange={filter.onChangeFilterKeyword}
                          onRemoveTerm={filter.onRemoveFilterKeyword}
                          resetVal={resetVal}
                          onSearchTypeChange={filter.onSearchTypeChange}
                          isFuzzySearch={filter.isFuzzySearch}
                          hasExactSearch={filter.hasExactSearch}
                        />
                      </SearchWrapper>
                    );

                  // defaults to checkboxes
                  default:
                    return (
                      <CheckboxList
                        options={filter.options}
                        onChange={props.onChangeFilter}
                        summaryLabel={paramsKey(filter.label)}
                        selectedOptions={getParamsValue(
                          paramsKey(filter.label),
                        )}
                        currPage={filter.currPage || 1}
                        totalPages={filter?.totalPages || 1}
                        updatePage={(page: number) =>
                          props.updatePage && props.updatePage(page)
                        }
                        searchable={filter.searchable}
                        resetVal={resetVal}
                        setResetVal={setResetVal}
                      />
                    );
                }
              })()}

              {filter.customElement}
            </Details>
            {filter.customCta}
          </Accordion>
        ))}
      </FilterAccordionWrapper>
    </>
  );
});
